import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ClockIcon, CheckIcon } from './Icons';

const ReasonSelection = ({ student, onReasonSelected, onBack, loading, sheetsService, selectedClass }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [signoutCount, setSignoutCount] = useState(null);

  const reasons = [
    { id: 'restroom', label: 'Restroom', icon: '🚽', color: 'bg-green-100 text-green-800 border-green-200' },
    { id: 'office', label: 'Office', icon: '🏢', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { id: 'other', label: 'Other', icon: '📝', color: 'bg-gray-100 text-gray-800 border-gray-200' }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch signout count when component mounts
  useEffect(() => {
    if (student?.name && selectedClass && sheetsService) {
      fetchSignoutCount();
    }
  }, [student, selectedClass, sheetsService]);

  const fetchSignoutCount = async () => {
    try {
      const count = await sheetsService.getStudentSignoutCount(student.name, selectedClass);
      setSignoutCount(count);
    } catch (error) {
      console.error('Error fetching signout count:', error);
      setSignoutCount(0);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleReasonSelect = (reasonId) => {
    setSelectedReason(reasonId);
    if (reasonId !== 'other') {
      setOtherReason('');
    }
  };

  const handleSubmit = () => {
    if (!selectedReason) {
      alert('Please select a reason');
      return;
    }

    if (selectedReason === 'other' && !otherReason.trim()) {
      alert('Please provide details for other reason');
      return;
    }

    const reasonLabel = reasons.find(r => r.id === selectedReason)?.label || '';
    onReasonSelected(reasonLabel, otherReason);
  };

  const isSubmitDisabled = !selectedReason || (selectedReason === 'other' && !otherReason.trim());

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={onBack}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Select Reason</h1>
              <p className="text-gray-600 mt-1">Why is {student?.name} leaving?</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Time Display */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="text-center">
            <div className="text-3xl font-mono text-gray-900">
              {formatTime(currentTime)}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {formatDate(currentTime)}
            </div>
          </div>
        </div>
      </div>

      {/* Student Info */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-semibold">
                {student?.name?.charAt(0) || 'S'}
              </span>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">{student?.name}</h3>
              <p className="text-sm text-gray-500">{student?.email}</p>
              {signoutCount !== null ? (
                <div className="text-xs text-blue-600 font-medium mt-1">
                  {signoutCount} signout{signoutCount !== 1 ? 's' : ''} in past 28 days for {selectedClass}
                </div>
              ) : (
                <div className="text-xs text-gray-400 mt-1">
                  Loading signout history...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Reason Selection */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Select a reason for leaving:</h3>
          
          <div className="space-y-4 mb-8">
            {reasons.map((reason) => (
              <div
                key={reason.id}
                onClick={() => handleReasonSelect(reason.id)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedReason === reason.id
                    ? 'border-primary bg-blue-50 ring-2 ring-primary ring-opacity-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">{reason.icon}</span>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="text-lg font-medium text-gray-900">
                      {reason.label}
                    </div>
                  </div>
                  {selectedReason === reason.id && (
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                        <CheckIcon className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Other Reason Input */}
          {selectedReason === 'other' && (
            <div className="mb-8">
              <label htmlFor="otherReason" className="block text-sm font-medium text-gray-700 mb-2">
                Please specify the reason:
              </label>
              <textarea
                id="otherReason"
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                placeholder="Enter the specific reason..."
                autoFocus
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onBack}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled || loading}
              className="px-6 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Recording...
                </>
              ) : (
                <>
                  <ClockIcon className="w-4 h-4 mr-2" />
                  Record Signout
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReasonSelection;
